import {
  type AppointmentLoadingType,
  type AppointmentMode,
  type AppointmentStatus,
  type AppointmentType,
} from 'core/appointments/domain/Appointment'
import {
  mapAppointmentToApi,
  mapOpenDockAppointmentToApi,
} from 'core/appointments/infra/appointmentMapper'
import { type AgnosticShipper } from 'core/domain/AgnosticShipper'
import { type ListResults } from 'core/domain/Request'
import { API_URL } from 'core/infra/environment'
import {
  HttpHeaders,
  type HttpClientConfig,
  type HttpClientInterface,
} from 'core/infra/http/HttpClient'
import { stringifyQueryString } from 'core/infra/queryString'
import { isString } from 'core/utils/string'

import { type AgnosticShipment } from '../domain/AgnosticShipment'
import { type AgnosticShipmentFilter } from '../domain/AgnosticShipmentFilter'
import { type AgnosticShipmentSort } from '../domain/AgnosticShipmentSort'
import { DocumentType } from '../domain/Document'
import { type Location } from '../domain/Location'
import type { EffectiveCarrier, Shipment } from '../domain/Shipment'
import { type ScheduleFormValues } from '../ui/OpenDockAppointmentDrawer/Schedule'
import { mapAgnosticShipmentFilterToApi } from './AgnosticShipmentFilterMapper'
import { mapApiToAgnosticShipment, type RawAgnosticShipment } from './AgnosticShipmentMapper'
import { mapAgnosticShipmentSortToApi } from './AgnosticShipmentSortMapper'
import { mapApiToShipper, type RawAgnosticShipper } from './AgnosticShipperMapper'
import {
  mapApiToInvoices,
  mapApiToPOD,
  mapApiToShipment,
  mapDriverLocationToApi,
  mapShipmentStatusToApi,
  type RawCertificate,
  type RawInvoice,
  type RawPOD,
  type RawShipment,
  mapApiToCertificates,
  type RawOpenDockCarrierId,
  type RawOpenDockWarehouseDetails,
  mapApiToOpenDockWarehouseDetails,
  mapApiToWarehouseAvailability,
  type RawOpenDockWarehouseAvailability,
  mapOpenDockWarehouseAvailabilityToApi,
  type RawOpenDockWarehouseFormFields,
  mapApiToWarehouseFormFields,
  mapOpenDockWarehouseAppointmentToApi,
  type OpenDockWarehouseFormFields,
} from './ShipmentMapper'

export const SHIPMENTS_PAGE_SIZE = 20

type ShipmentRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export type AssignDriverParams = {
  driverUUID: string
  shipmentUUID: string
  config?: Partial<HttpClientConfig>
}

export type UnassignDriverParams = {
  shipmentUUID: string
  config?: Partial<HttpClientConfig>
}

export type AssignAssetCarrierProps = {
  effectiveCarrier: EffectiveCarrier
  shipmentUUID: string
  config?: Partial<HttpClientConfig>
}

type UpdateShipmentStatusParams = {
  shipmentUUID: string
  stopId: string | number
  type: string
  date: string
  time: string
}

export type UpdateShipmentTractorNumberParams = {
  shipmentUUID: string
  tractorNumber: string | null
  config?: Partial<HttpClientConfig>
}

export type FileUploadParams = {
  file: File
  shipmentUUID: string
  config?: Partial<HttpClientConfig>
}

export type UploadInvoiceParams = {
  total: number
  number?: string
} & FileUploadParams

export type UploadDocumentParams = {
  documentType:
    | DocumentType.POD
    | DocumentType.Invoice
    | DocumentType.WeightCertificate
    | DocumentType.InspectionCertificate
} & (FileUploadParams | UploadInvoiceParams)

export type DriverLocationParams = {
  shipmentUUID: string
  city: string
  state: string
  latitude: string
  longitude: string
  date: string
  time: string
  config?: Partial<HttpClientConfig>
}

export type DeleteDriverLocationParams = {
  shipmentUUID: string
  locationUUID: string
  config?: Partial<HttpClientConfig>
}

export type GetAgnosticShipmentsListParams = {
  config?: Partial<HttpClientConfig>
  page?: number
  pageSize?: number
  next?: string | null
  filter?: AgnosticShipmentFilter
  sort?: AgnosticShipmentSort
}

export type AppointmentCommonFormValues = {
  confirmationNumber?: string | null
  misschedulingReason: {
    label: string
    value: string
    _type?: string
  }
  misschedulingNote: string | undefined
}

export type AppointmentFormValues = AppointmentCommonFormValues & {
  type: AppointmentType
  mode: AppointmentMode
  loadingType: AppointmentLoadingType
  notifyShipper: boolean
  status: AppointmentStatus
  timeType: {
    label: string
    value: string
    _type?: string
  } | null
  date: string
  time: string
  timeEnd: string
}

export type AppointmentFormValuesWithRequiredFields = Omit<AppointmentFormValues, 'timeType'> & {
  timeType: NonNullable<AppointmentFormValues['timeType']>
}

type UpdateAppointmentParams = {
  shipmentUUID: string
  stopIndex: number
  data: AppointmentFormValuesWithRequiredFields
  config?: Partial<HttpClientConfig>
}

export type AppointmentOpenDockFormValues = Partial<AppointmentCommonFormValues>

type UpdateOpenDockAppointmentParams = {
  shipmentUUID: string
  stopIndex: number
  data: AppointmentOpenDockFormValues
  config?: Partial<HttpClientConfig>
}

export type RawProviderTrackingLink = {
  tracking_link: string | null
}

export class ShipmentRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ShipmentRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getShipmentByUUID(uuid: string, config?: Partial<HttpClientConfig>): Promise<Shipment> {
    const endpoint = `/shipments/${uuid}`

    const { data } = await this.httpClient.get<RawShipment>(endpoint, config)

    return mapApiToShipment(data)
  }

  async updateShipmentStatus(
    shipmentStatus: UpdateShipmentStatusParams,
    config?: Partial<HttpClientConfig>
  ): Promise<void> {
    const { shipmentUUID, stopId } = shipmentStatus

    const endpoint = `shipments/${shipmentUUID}/stops/${stopId}/events`

    await this.httpClient.post(endpoint, mapShipmentStatusToApi(shipmentStatus), config)
  }

  async downloadBOL(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${uuid}/bill_of_lading.pdf`

    const { data } = await this.httpClient.get(endpoint, {
      ...config,
      responseType: 'blob',
    })

    return data
  }

  async downloadRatecon(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${uuid}/ratecon.pdf`

    const { data } = await this.httpClient.get(endpoint, {
      ...config,
      responseType: 'blob',
    })

    return data
  }

  async getProviderTrackingLink(uuid: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${uuid}/provider-tracking-link`

    const { data } = await this.httpClient.get<RawProviderTrackingLink>(endpoint, config)

    return data.tracking_link
  }

  async assignDriverToShipment({ driverUUID, shipmentUUID, config }: AssignDriverParams) {
    const endpoint = `/shipments/${shipmentUUID}`

    const payload = { driver_uuid: driverUUID }

    await this.httpClient.patch(endpoint, payload, config)
  }

  async unassignDriver({ shipmentUUID, config }: UnassignDriverParams) {
    const endpoint = `/shipments/${shipmentUUID}/driver`

    await this.httpClient.delete(endpoint, config)
  }

  async assignAssetCarrierToShipment({
    effectiveCarrier,
    shipmentUUID,
    config,
  }: AssignAssetCarrierProps): Promise<any> {
    const endpoint = `/shipments/${shipmentUUID}/effective-carrier`

    await this.httpClient.post(endpoint, effectiveCarrier, config)
  }

  async updateAssetCarrierToShipment({
    effectiveCarrier,
    shipmentUUID,
    config,
  }: AssignAssetCarrierProps): Promise<any> {
    const endpoint = `/shipments/${shipmentUUID}/effective-carrier`

    await this.httpClient.put(endpoint, effectiveCarrier, config)
  }

  async updateShipmentTractorNumber({
    shipmentUUID,
    tractorNumber,
    config,
  }: UpdateShipmentTractorNumberParams) {
    const endpoint = `/shipments/${shipmentUUID}`
    const payload = { tractor_number: tractorNumber }

    await this.httpClient.patch(endpoint, payload, config)
  }

  async uploadPOD({ shipmentUUID, file, config }: FileUploadParams): Promise<void> {
    const endpoint = `/shipments/${shipmentUUID}/proof-of-delivery`

    const data = new FormData()
    data.append('file', file)

    await this.httpClient.post(endpoint, data, {
      ...config,
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    })

    return undefined
  }

  async uploadWeightCertificate({ shipmentUUID, file, config }: FileUploadParams): Promise<void> {
    const endpoint = `/shipments/${shipmentUUID}/weight-certificate`

    const data = new FormData()
    data.append('file', file)

    await this.httpClient.post(endpoint, data, {
      ...config,
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    })
  }

  async uploadInspectionCertificate({
    shipmentUUID,
    file,
    config,
  }: FileUploadParams): Promise<void> {
    const endpoint = `/shipments/${shipmentUUID}/inspection-certificate`

    const data = new FormData()
    data.append('file', file)

    await this.httpClient.post(endpoint, data, {
      ...config,
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    })
  }

  async addDriverLocation(data: DriverLocationParams) {
    const { shipmentUUID, config } = data
    const endpoint = `shipments/${shipmentUUID}/locations`

    await this.httpClient.post(endpoint, mapDriverLocationToApi(data), config)
  }

  async getPODs(shipmentUUID: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${shipmentUUID}/proofs-of-delivery`

    const { data } = await this.httpClient.get<RawPOD[]>(endpoint, config)
    try {
      return data.map(rawPOD => mapApiToPOD(rawPOD))
    } catch (error) {
      throw new Error('Failed to map API return to PODs', { cause: error as Error })
    }
  }

  async getDriverLocations(
    shipmentUUID: string,
    config?: Partial<HttpClientConfig>
  ): Promise<Location[]> {
    const endpoint = `/shipments/${shipmentUUID}/locations`

    const {
      data: { data },
    } = await this.httpClient.get(endpoint, config)

    return data
  }

  async deleteDriverLocation({ shipmentUUID, locationUUID, config }: DeleteDriverLocationParams) {
    const endpoint = `/shipments/${shipmentUUID}/locations/${locationUUID}`

    const { data } = await this.httpClient.delete(endpoint, config)

    return data
  }

  async getAgnosticShipmentsList(
    params: GetAgnosticShipmentsListParams = {}
  ): Promise<ListResults<AgnosticShipment>> {
    const { config, next } = params

    let endpoint: string

    if (isString(next)) {
      endpoint = next.replace(API_URL, '')
    } else {
      const { page = 1, pageSize = SHIPMENTS_PAGE_SIZE, filter, sort } = params
      const queryParams = stringifyQueryString(
        {
          page,
          page_size: pageSize,
          sort: mapAgnosticShipmentSortToApi(sort),
          ...mapAgnosticShipmentFilterToApi(filter),
        },
        {
          encode: true,
        }
      )

      endpoint = `/api/v2/shipperguide/shipments?${queryParams}`
    }

    const { data } = await this.httpClient.get<ListResults<RawAgnosticShipment>>(endpoint, config)

    return {
      ...data,
      results: data.results.map(mapApiToAgnosticShipment),
    }
  }

  async getAgnosticShippers(config?: Partial<HttpClientConfig>): Promise<AgnosticShipper[]> {
    const endpoint = '/api/v2/shipperguide/shippers'

    const { data } = await this.httpClient.get<Array<RawAgnosticShipper>>(endpoint, config)

    return data.map(mapApiToShipper)
  }

  async uploadInvoice({
    shipmentUUID,
    file,
    total,
    number,
    config,
  }: UploadInvoiceParams): Promise<void> {
    const endpoint = `/shipments/${shipmentUUID}/invoices`

    const data = new FormData()
    data.append('file', file)
    data.append('total', total.toString())
    data.append('number', number || '')

    await this.httpClient.post(endpoint, data, {
      ...config,
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    })
  }

  async getInvoices(shipmentUUID: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${shipmentUUID}/invoices`

    const { data } = await this.httpClient.get<Array<RawInvoice>>(endpoint, config)

    return mapApiToInvoices(data)
  }

  async updateAppointment({ shipmentUUID, stopIndex, data, config }: UpdateAppointmentParams) {
    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/appointment`

    return this.httpClient.put(endpoint, mapAppointmentToApi(data), config)
  }

  async updateOpenDockAppointment({
    shipmentUUID,
    stopIndex,
    data,
    config,
  }: UpdateOpenDockAppointmentParams) {
    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/appointment`

    const payload = mapOpenDockAppointmentToApi(data)

    return this.httpClient.put(endpoint, payload, config)
  }

  async uploadDocument(params: UploadDocumentParams): Promise<void> {
    const { documentType } = params

    switch (documentType) {
      case DocumentType.POD: {
        const { shipmentUUID, file, config } = params
        return this.uploadPOD({ shipmentUUID, file, config })
      }
      case DocumentType.WeightCertificate: {
        const { shipmentUUID, file, config } = params
        return this.uploadWeightCertificate({ shipmentUUID, file, config })
      }
      case DocumentType.InspectionCertificate: {
        const { shipmentUUID, file, config } = params
        return this.uploadInspectionCertificate({ shipmentUUID, file, config })
      }
      case DocumentType.Invoice: {
        const { shipmentUUID, file, total, number, config } = params as UploadInvoiceParams
        return this.uploadInvoice({ shipmentUUID, file, total, number, config })
      }
    }
  }

  async updateProNumber(
    shipmentUUID: string,
    proNumber?: string,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/shipments/${shipmentUUID}/carrier-reference/`

    const pro_number = proNumber !== '' ? proNumber : null

    return this.httpClient.patch(
      endpoint,
      {
        pro_number,
      },
      config
    )
  }

  async getWeightCertificates(shipmentUUID: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${shipmentUUID}/weight-certificates`

    const { data } = await this.httpClient.get<Array<RawCertificate>>(endpoint, config)

    return mapApiToCertificates(data)
  }

  async getInspectionCertificates(shipmentUUID: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${shipmentUUID}/inspection-certificates`

    const { data } = await this.httpClient.get<Array<RawCertificate>>(endpoint, config)

    return mapApiToCertificates(data)
  }

  async getOpenDockCarrierId(shipmentUUID: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `/shipments/${shipmentUUID}/opendock-carrier`

    const { data } = await this.httpClient.get<RawOpenDockCarrierId>(endpoint, config)

    return data.id
  }

  async getOpenDockWarehouseDetails(
    shipmentUUID: string,
    stopIndex: number,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/warehouse`

    const { data } = await this.httpClient.get<RawOpenDockWarehouseDetails>(endpoint, config)

    return mapApiToOpenDockWarehouseDetails(data)
  }

  async getOpenDockWarehouseAvailability(
    shipmentUUID: string,
    stopIndex: number,
    params: {
      loadTypeId: string
      dockId: string
      appointmentDate: string
      facilityTimezone?: string
    },
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/warehouse/availability`

    const { data } = await this.httpClient.post<RawOpenDockWarehouseAvailability>(
      endpoint,
      mapOpenDockWarehouseAvailabilityToApi(params),
      config
    )

    return mapApiToWarehouseAvailability(data, params.facilityTimezone)
  }

  async getOpenDockWarehouseFormFields(
    shipmentUUID: string,
    stopIndex: number,
    params: {
      loadTypeId: string
    },
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/warehouse/form-fields`

    const { data } = await this.httpClient.post<RawOpenDockWarehouseFormFields>(
      endpoint,
      {
        load_type_id: params.loadTypeId,
      },
      config
    )

    return mapApiToWarehouseFormFields(data)
  }

  async uploadToOpenDockStorage(
    shipmentUUID: string,
    file: File,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `/shipments/${shipmentUUID}/opendock-storage`

    const formData = new FormData()
    formData.append('file', file)

    const { data } = await this.httpClient.post<{
      key: string
      url: string
    }>(endpoint, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })

    return data
  }

  async createOpenDockWarehouseAppointment(
    shipmentUUID: string,
    stopIndex: number,
    params: {
      staticFields: ScheduleFormValues
      warehouseFormFields: OpenDockWarehouseFormFields
      openDockCarrierId: string | undefined
      facilityTimezone: string
    },
    config?: Partial<HttpClientConfig>
  ) {
    const payload = mapOpenDockWarehouseAppointmentToApi(params)

    const endpoint = `/shipments/${shipmentUUID}/stops/${stopIndex}/warehouse/appointment`

    const { data } = await this.httpClient.post(endpoint, payload, config)

    return data
  }
}
